import {
  curry,
  difference,
  equals,
  isEmpty,
  pathOr,
  prop,
  __,
} from 'ramda';
import * as actions from '../../actions';
import setKey from './setKey';
import setNewKey from './setNewKey';
import { Key, KeyState } from '../../models';
import { qualifyKeyPath } from './utils';

const updateKey = curry((state, domain, key) => {
  const keyPath = prop('path', key);
  const qualifiedPath = qualifyKeyPath(domain, keyPath);
  const previousKey = pathOr(Key.from({
    path: keyPath,
    basicAuth: key.basicAuth,
  }), qualifiedPath, state);

  const differences = difference(
    [prop('data', key), prop('readers', key), prop('writers', key)],
    [prop('data', previousKey), prop('readers', previousKey), prop('writers', previousKey)],
  );
  const previousState = prop('state', previousKey);
  const currentState = prop('state', key);
  const keyWasUpdated = (
    equals(previousState, KeyState.CONSISTENT)
    && !equals(currentState, KeyState.EMPTY)
    && !isEmpty(differences)
  );
  const keyState = keyWasUpdated ? KeyState.MODIFIED : prop('state', key);

  return key.set({ state: keyState });
});

export default (state, action) => {
  const actionProp = prop(__, action);

  switch (actionProp('type')) {
    case actions.CREATE_NEW_KEY: {
      const domain = actionProp('domain');
      const useBasicAuth = actionProp('useBasicAuth');
      const newKey = Key.from({ isSelected: true, basicAuth: useBasicAuth });

      return setNewKey(state, domain, newKey);
    }
    case actions.DISCARD_NEW_KEY: {
      const domain = actionProp('domain');
      const useBasicAuth = actionProp('useBasicAuth');
      const newKey = Key.from({ basicAuth: useBasicAuth });

      return setNewKey(state, domain, newKey);
    }
    case actions.UPDATE_NEW_KEY: {
      const domain = actionProp('domain');
      const key = actionProp('key');
      const updatedKey = key.set({ state: KeyState.MODIFIED });

      return setNewKey(state, domain, updatedKey);
    }
    case actions.UPDATE_KEY: {
      const domain = actionProp('domain');
      const key = actionProp('key');
      const updatedKey = updateKey(state, domain, key);
      const keyPath = prop('path', key);

      return setKey({ ...state }, domain, keyPath, updatedKey);
    }
    case actions.REMOVE_KEY: {
      const domain = actionProp('domain');
      const path = actionProp('path');

      return setKey({ ...state }, domain, path, undefined);
    }
    default:
      return state;
  }
};
